import { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../services/api';
import { incidentsApi } from '../services/incidentsApi';
import {
  buildEvidenceThumbEmptyLabel,
  initialEvidenceThumbState,
  listIncidentsWithAvailableSnapshot,
  OVERVIEW_EVIDENCE_THUMB_INCIDENT_LIMIT,
  OVERVIEW_EVIDENCE_THUMB_TIMEOUT_MS,
  overviewEvidenceThumbListDateProbes,
  overviewEvidenceThumbRequestKey,
  type OverviewEvidenceThumbRequest,
  type OverviewEvidenceThumbState,
} from '../domain/continuousSafety/overviewConditionEvidenceThumb';

export type OverviewConditionEvidenceThumbsState = {
  byConditionId: Record<string, OverviewEvidenceThumbState>;
};

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = window.setTimeout(() => {
      reject(new Error('overview_evidence_thumb_timeout'));
    }, ms);
    promise.then(
      (value) => {
        window.clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        window.clearTimeout(timer);
        reject(error);
      },
    );
  });
}

async function fetchLatestSnapshotObjectUrl(incidentId: string): Promise<string> {
  const res = await api.get(
    `/api/v1/incidents/${encodeURIComponent(incidentId)}/evidence/latest/snapshot`,
    { responseType: 'blob' },
  );
  const blob = res.data as Blob;
  // Guard against auth/HTML error bodies returned as opaque blobs.
  if (!blob || blob.size < 32) {
    throw new Error('overview_evidence_thumb_empty_blob');
  }
  if (blob.type && !String(blob.type).startsWith('image/')) {
    throw new Error(`overview_evidence_thumb_non_image:${blob.type}`);
  }
  return URL.createObjectURL(blob);
}

/**
 * Load real Investigate evidence stills for Overview condition rows.
 * Only emits imageUrl when the snapshot endpoint returns an image blob for an
 * AVAILABLE/READY (or uri-backed) incident in that condition's scope.
 * Timeouts resolve to an honest empty/camera label — never spin forever.
 *
 * Fast path: undated untyped zone/camera listIncidents (~1s on yyz) + client
 * ranking by preferred stored types. Avoids:
 * - listIncidents(?incident_type=zone_violation) — minutes on yyz
 * - listIncidents(?from_date&to_date multi-day) — can hang >20s with 0 bytes
 *   and starve the 12s thumb budget before any still loads
 */
export function useOverviewConditionEvidenceThumbs(params: {
  siteId: string;
  requests: OverviewEvidenceThumbRequest[];
  enabled?: boolean;
}): OverviewConditionEvidenceThumbsState {
  const { siteId, requests, enabled = true } = params;
  const [byConditionId, setByConditionId] = useState<Record<string, OverviewEvidenceThumbState>>({});
  const objectUrlsRef = useRef<Set<string>>(new Set());
  const generationRef = useRef(0);

  const requestKey = useMemo(
    () => requests.map(overviewEvidenceThumbRequestKey).join('||'),
    [requests],
  );

  useEffect(() => {
    const generation = generationRef.current + 1;
    generationRef.current = generation;

    const revokeAll = () => {
      Array.from(objectUrlsRef.current).forEach((url) => {
        try {
          URL.revokeObjectURL(url);
        } catch {
          // ignore
        }
      });
      objectUrlsRef.current.clear();
    };

    if (!enabled || !siteId || requests.length === 0) {
      revokeAll();
      setByConditionId({});
      return () => {
        generationRef.current += 1;
        revokeAll();
      };
    }

    const loadingMap: Record<string, OverviewEvidenceThumbState> = {};
    for (const request of requests) {
      loadingMap[request.conditionId] = {
        ...initialEvidenceThumbState(request),
        status: 'loading',
      };
    }
    setByConditionId(loadingMap);

    void (async () => {
      const loadOne = async (
        request: OverviewEvidenceThumbRequest,
      ): Promise<OverviewEvidenceThumbState> => {
        const emptyLabel = buildEvidenceThumbEmptyLabel(request);
        const emptyState: OverviewEvidenceThumbState = {
          status: 'empty',
          imageUrl: null,
          emptyLabel,
          incidentId: null,
        };
        const preferredTypes = (request.candidateIncidentTypes && request.candidateIncidentTypes.length
          ? request.candidateIncidentTypes
          : (request.incidentType ? [request.incidentType] : [])
        ).map((value) => String(value || '').trim().toLowerCase()).filter(Boolean);

        const listFor = async (opts: {
          fromDate?: string;
          toDate?: string;
        }) => {
          const { incidents } = await incidentsApi.listIncidents(
            undefined,
            undefined,
            OVERVIEW_EVIDENCE_THUMB_INCIDENT_LIMIT,
            0,
            opts.fromDate,
            opts.toDate,
            undefined,
            request.zoneId || undefined,
            undefined,
            request.cameraId || undefined,
            { skip_total: true },
          );
          return incidents;
        };

        const prefer = {
          zoneId: request.zoneId,
          cameraId: request.cameraId,
          preferredIncidentTypes: preferredTypes,
        };

        const candidates: Array<{ incident_id: string }> = [];
        const seen = new Set<string>();
        const pushCandidates = (incidents: Awaited<ReturnType<typeof listFor>>) => {
          for (const incident of listIncidentsWithAvailableSnapshot(incidents, prefer)) {
            const id = String(incident.incident_id || '').trim();
            if (!id || seen.has(id)) continue;
            seen.add(id);
            candidates.push({ incident_id: id });
          }
        };

        for (const dateProbe of overviewEvidenceThumbListDateProbes(request)) {
          if (generation !== generationRef.current) return emptyState;
          if (candidates.length > 0) break;
          try {
            pushCandidates(await withTimeout(
              listFor(dateProbe),
              // Per-probe cap: never let one hung listIncidents burn the whole
              // Overview thumb budget (overall timeout still applies).
              Math.min(OVERVIEW_EVIDENCE_THUMB_TIMEOUT_MS, 8_000),
            ));
          } catch {
            // Timed-out / failed list probe — try next plan step / groups.
          }
        }

        // Pattern groups — representative incident_id when list probe misses.
        if (candidates.length === 0) {
          if (generation !== generationRef.current) return emptyState;
          try {
            const groups = await incidentsApi.listIncidentGroups({
              status: 'all',
              limit: 20,
              zone_id: request.zoneId || undefined,
              camera_id: request.cameraId || undefined,
              prefer_persisted: true,
            });
            for (const group of groups.items || []) {
              const groupType = String(
                (group as { incident_type?: string | null }).incident_type || '',
              ).trim().toLowerCase();
              if (
                preferredTypes.length > 0
                && groupType
                && !preferredTypes.includes(groupType)
              ) {
                continue;
              }
              const id = String(
                (group as { incident_id?: string | null }).incident_id
                || (group as { linked_detailed_record_id?: string | null }).linked_detailed_record_id
                || '',
              ).trim();
              if (!id || seen.has(id)) continue;
              seen.add(id);
              candidates.push({ incident_id: id });
            }
          } catch {
            // Groups are a best-effort fallback only.
          }
        }

        for (const candidate of candidates) {
          if (generation !== generationRef.current) return emptyState;
          try {
            const imageUrl = await fetchLatestSnapshotObjectUrl(candidate.incident_id);
            if (generation !== generationRef.current) {
              try {
                URL.revokeObjectURL(imageUrl);
              } catch {
                // ignore
              }
              return emptyState;
            }
            objectUrlsRef.current.add(imageUrl);
            return {
              status: 'ready',
              imageUrl,
              emptyLabel,
              incidentId: candidate.incident_id,
            };
          } catch {
            // Try the next AVAILABLE/READY incident — URL/CORS/blob failures must not
            // blank a condition that has other real stills.
          }
        }

        return emptyState;
      };

      await Promise.all(
        requests.map(async (request) => {
          const emptyLabel = buildEvidenceThumbEmptyLabel(request);
          const emptyState: OverviewEvidenceThumbState = {
            status: 'empty',
            imageUrl: null,
            emptyLabel,
            incidentId: null,
          };
          let state = emptyState;
          try {
            state = await withTimeout(loadOne(request), OVERVIEW_EVIDENCE_THUMB_TIMEOUT_MS);
          } catch {
            state = emptyState;
          }
          if (generation !== generationRef.current) return;
          setByConditionId((prev) => ({
            ...prev,
            [request.conditionId]: state,
          }));
        }),
      );
    })();

    return () => {
      generationRef.current += 1;
      revokeAll();
    };
    // requestKey captures requests identity; siteId/enabled gate the effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, siteId, requestKey]);

  return { byConditionId };
}
